import { yupResolver } from "@hookform/resolvers/yup";
import { notify } from "@/shared/utils/notify";
import moment from "moment";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';

import { validateCreateBanners, validateUpdateBanners } from "../../../helpers";
import { bannersChangeModalState, createBanner, refreshTableBanners, selectBannersForUpdate, selectBannersRefreshTable, selectIsUpdateBanners, setIsBannersForUpdate, updateBanner } from "../../../redux";
import { DataBanner } from "../../../redux/@types";
import useAppDispatch from "../../../redux/hooks/useAppDispatch";
import { TableProperties } from "../../../shared/components/ui/Table/types";
import { PROMO_ACTION_PREFIX } from "../../../shared/components/ui/modals/ModalBanners";

function encodePromoActionId(
  actionType?: string | null,
  actionId?: string | number | null,
): string | undefined {
  if (actionId == null || actionId === "") return undefined;
  if (actionType === "FIFTY_OFF") {
    return `${PROMO_ACTION_PREFIX.fifty}${actionId}`;
  }
  if (actionType === "PROMOTION") {
    return `${PROMO_ACTION_PREFIX.rule}${actionId}`;
  }
  return String(actionId);
}

function resolvePromoAction(data: DataBanner): {
  action_type: string;
  action_id: number | null;
} {
  const raw = data.action_id != null ? String(data.action_id) : "";
  if (raw.startsWith(PROMO_ACTION_PREFIX.fifty)) {
    return {
      action_type: "FIFTY_OFF",
      action_id: Number(raw.slice(PROMO_ACTION_PREFIX.fifty.length)) || null,
    };
  }
  if (raw.startsWith(PROMO_ACTION_PREFIX.rule)) {
    return {
      action_type: "PROMOTION",
      action_id: Number(raw.slice(PROMO_ACTION_PREFIX.rule.length)) || null,
    };
  }
  return {
    action_type: data.action_type,
    action_id:
      raw !== "" && !Number.isNaN(Number(raw)) ? Number(raw) : null,
  };
}

export const useBanners = () => {
  const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const isUpdateBanner = useSelector(
        selectIsUpdateBanners
      );
      const dataUpdateBanner = useSelector(
        selectBannersForUpdate
      );
      const isRefreshTableBanners = useSelector(
        selectBannersRefreshTable
      );

      const [isLoading, setIsLoading] = useState(false);
      const [tableProps, setTableProps] = useState<TableProperties>();


      const changeModalBannersState = () => {
        dispatch(setIsBannersForUpdate(false));
        dispatch(bannersChangeModalState());
      };

      const changeNavToBannersProducts = (id: number) => {
        navigate(`/bannersProducts/${id}`);
      };

      const formMethodsCreate = useForm<DataBanner>({
        resolver: yupResolver(validateCreateBanners),
      });

      const formMethodsUpdate = useForm<DataBanner>({
        resolver: yupResolver(validateUpdateBanners),
      });

      useEffect(() => {
        if (isUpdateBanner) {
          formMethodsCreate.reset();
          const isFiftyOff = dataUpdateBanner?.action_type === "FIFTY_OFF";
          formMethodsUpdate.reset({
            ...dataUpdateBanner,
            // En el formulario, Rollos 50% se elige dentro de "PROMOCIÓN"
            action_type: isFiftyOff
              ? "PROMOTION"
              : dataUpdateBanner?.action_type,
            action_id: encodePromoActionId(
              dataUpdateBanner?.action_type,
              dataUpdateBanner?.action_id,
            ) as any,
            start_date: dataUpdateBanner?.start_date
              ? moment(dataUpdateBanner.start_date).format("YYYY-MM-DD HH:mm:ss")
              : undefined,
            end_date: dataUpdateBanner?.end_date
              ? moment(dataUpdateBanner.end_date).format("YYYY-MM-DD HH:mm:ss")
              : undefined,
          });
        }

        return () => {
          formMethodsUpdate.reset();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [isUpdateBanner, dataUpdateBanner]);

      const updateStatus = async(value: "active" | "inactive", id: any) =>{
        if (isLoading) return;
        setIsLoading(true);
        await updateBanner({
          id,
          status: value,
        })
        .then((res: Partial<DataBanner>) => {
          setIsLoading(false);
          if (res.id) {
            // dispatch(bannersChangeModalState());
            notify.success(
              `Se ha actualizado con éxito el estado del banner con id:${res.id}`
            );
            dispatch(refreshTableBanners(true));
          } else {
            notify.error(res as string);
          }
        })
        .catch((err: any) => {
          setIsLoading(false);
          console.log(err);
          notify.error(err.message);
        });
      }

    const formatDateSafe = (value: any) => {
      if (value == null || value === "") return null;
      const m = moment(value);
      return m.isValid() ? m.format("YYYY-MM-DD HH:mm:ss") : null;
    };

    const onSubmitCreateOrUpdate = async (data: DataBanner) => {
        if (isLoading) return;
        setIsLoading(true);
        if (isUpdateBanner) {
          console.log("Actualiando===>", data);
          const { createdAt: _createdAt, updatedAt: _updatedAt, deletedAt: _deletedAt, id: _id, start_date: _sd, end_date: _ed, ...dataToUpdate } = data;
          const promoAction = resolvePromoAction(data);
          const payload: any = {
            id: data.id,
            ...dataToUpdate,
            path_movile: data.path_movile ?? null,
            path_desktop: data.path_desktop ?? null,
            action_type: promoAction.action_type,
            action_id: promoAction.action_id,
          };
          if (data.type === "FLASH_SALE") {
            const sd = formatDateSafe(data.start_date);
            const ed = formatDateSafe(data.end_date);
            if (sd != null) payload.start_date = sd;
            if (ed != null) payload.end_date = ed;
          }
          await updateBanner(payload)
            .then((res: Partial<DataBanner>) => {
              setIsLoading(false);
              if (res.id) {
                changeModalBannersState();
                notify.success(
                  `Se ha actualizado con éxito el banner con id:${res.id}`
                );
                dispatch(refreshTableBanners(true));
              } else {
                notify.error(res as string);
              }
            })
            .catch((err: any) => {
              setIsLoading(false);
              console.log(err);
              notify.error(err.message);
            });
        } else {
          const promoAction = resolvePromoAction(data);
          const payload: any = {
            ...data,
            path_movile: data.path_movile ?? null,
            path_desktop: data.path_desktop ?? null,
            action_type: promoAction.action_type,
            action_id: promoAction.action_id,
          };
          if (data.type === "FLASH_SALE") {
            const sd = formatDateSafe(data.start_date);
            const ed = formatDateSafe(data.end_date);
            payload.start_date = sd;
            payload.end_date = ed;
          } else {
            delete payload.start_date;
            delete payload.end_date;
          }
          await createBanner(payload)
            .then((res: DataBanner) => {
              setIsLoading(false);
              if (res.id) {
                dispatch(bannersChangeModalState());
                notify.success(
                  `Se ha creado con éxito el banner con id:${res.id}`
                );
                dispatch(refreshTableBanners(true));
              } else {
                notify.error(res as any);
              }
            })
            .catch((err: any) => {
              setIsLoading(false);
              console.log(err);
              notify.error(err.message);
            });
        }
      };

      useEffect(() => {
        if (isRefreshTableBanners) {
          tableProps?.refresh();
          dispatch(refreshTableBanners(false));
        }
      // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [isRefreshTableBanners]);
  return {
    //state
    isUpdateBanner,
    isLoading,
    tableProps,
    //methods
    formMethodsCreate,
    formMethodsUpdate,
    setTableProps,
    //functions
    changeModalBannersState,
    onSubmitCreateOrUpdate,
    changeNavToBannersProducts,
    updateStatus,
  }
}
